import { createContext, useContext } from 'react'
import type { ClientConfig } from '../data/api'

/**
 * Zustand der Anmeldung.
 *
 * - loading: die Anmeldeparameter werden geholt oder der Rückweg von Authentik
 *   wird abgearbeitet.
 * - ready: es liegt ein gültiges Token vor.
 * - offline: es gab einmal eine Anmeldung, das Token ist abgelaufen und der
 *   Server ist nicht erreichbar. Die App läuft mit den lokalen Daten weiter,
 *   der Abgleich ruht.
 * - signed-out: es muss angemeldet werden.
 * - error: die Anmeldung ist gescheitert.
 */
export type AuthStatus = 'loading' | 'ready' | 'offline' | 'signed-out' | 'error'

export interface AuthContextValue {
  status: AuthStatus
  config: ClientConfig | null
  /**
   * Die stabile Kennung des angemeldeten Nutzers: der sub-Claim aus dem Token.
   * Leer, solange niemand angemeldet ist.
   *
   * Sie steht neben dem Anzeigenamen und nicht an seiner Stelle, weil beide
   * verschiedene Fragen beantworten. Der Name ist, was auf dem Papier steht;
   * an der Kennung hängt, wem ein Tagebuchbeitrag gehört. Namen ändern sich,
   * Kennungen nicht.
   */
  userId: string
  /** Anzeigename des angemeldeten Nutzers, soweit bekannt. */
  displayName: string
  /**
   * Die E-Mail-Adresse aus dem Token, soweit bekannt.
   *
   * An ihr hängt seit dem Reisemodell die Mitgliedschaft: berechtigt wird über
   * die Adresse und nicht über ein lokales Konto. Sie steht deshalb neben der
   * Kennung und nicht an ihrer Stelle - die Kennung sagt, wem ein Beitrag
   * gehört, die Adresse sagt, in welchen Reisen jemand mitfährt.
   *
   * Der belastbare Wert kommt aus /api/v1/me, denn dort entscheidet der Server;
   * dieser hier ist die Auskunft, die auch ohne Netz vorliegt.
   */
  email: string
  error: string | null
  /** Liefert ein gültiges Zugriffstoken, erneuert es bei Bedarf. */
  getToken: () => Promise<string | null>
  signIn: () => Promise<void>
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth ausserhalb von AuthProvider benutzt')
  return value
}
